import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { FacetData } from '~/contracts/common';
import type { Pagination } from '~/contracts/srp';
import usePriceDrop from '~/hooks/srp/usePriceDrop';
import { DeepPartial } from '~/types';
import { mergeFromBaseline, setPriceDropDisplayName } from './utils';

export type FacetDataContextType = {
  baseline: Partial<FacetData>;
  didExpandMenu: boolean;
  facetData: LoadableState<Partial<FacetData>>;
  pagination: Partial<Pagination>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<Pagination>>>;
  triggerMenuDidExpand: () => void;
};

const defaultContext = {
  baseline: {},
  didExpandMenu: false,
  facetData: defaultLoadableState<Partial<FacetData>>({}),
  pagination: {},
  setPagination() {},
  triggerMenuDidExpand() {}
};

const FacetDataContext = createContext<FacetDataContextType>(defaultContext);

export type OwnProps = {
  baseline?: Partial<FacetData>;
  facetData?: Partial<FacetData>;
  pagination?: Partial<Pagination>;
};

export function FacetDataContextProvider(
  props: React.PropsWithChildren<OwnProps>
) {
  const priceDropDisplayName = usePriceDrop().displayText;

  const baselineFromProps = useMemo(
    () => setPriceDropDisplayName(props.baseline ?? {}, priceDropDisplayName),
    [props.baseline, priceDropDisplayName]
  );
  const facetDataFromProps = useMemo(
    () =>
      props.facetData
        ? setPriceDropDisplayName(props.facetData ?? {}, priceDropDisplayName)
        : undefined,
    [priceDropDisplayName, props.facetData]
  );

  const [baseline, setBaseline] = useState<Partial<FacetData>>(
    setPriceDropDisplayName(baselineFromProps, priceDropDisplayName)
  );
  const facetData = useLoadableState<Partial<FacetData>>(
    useMemo<Partial<FacetData>>(
      () =>
        facetDataFromProps
          ? mergeFromBaseline(facetDataFromProps, baseline)
          : baselineFromProps,
      [baseline, baselineFromProps, facetDataFromProps]
    )
  );
  const [didExpandMenu, setDidExpandMenu] = useState(false);
  const [pagination, setPagination] = useState<Partial<Pagination>>(
    props.pagination ?? defaultContext.pagination
  );

  useEffect(() => {
    facetData.setData.call(
      null,
      facetDataFromProps
        ? mergeFromBaseline(facetDataFromProps, baselineFromProps)
        : baselineFromProps
    );
  }, [baselineFromProps, facetData.setData, facetDataFromProps]);

  useEffect(() => {
    setBaseline(prevState =>
      setPriceDropDisplayName(prevState, priceDropDisplayName)
    );
    facetData.setData.call(null, prevState =>
      setPriceDropDisplayName(prevState, priceDropDisplayName)
    );
  }, [facetData.setData, priceDropDisplayName]);

  useEffect(() => {
    setPagination(props.pagination ?? defaultContext.pagination);
  }, [props.pagination]);

  const setFacetDataWithMerge = useCallback<
    React.Dispatch<React.SetStateAction<Partial<FacetData>>>
  >(
    newFacetData => {
      facetData.setData.call(null, prevState =>
        mergeFromBaseline(
          setPriceDropDisplayName(
            typeof newFacetData === 'function'
              ? newFacetData(prevState)
              : newFacetData,
            priceDropDisplayName
          ),
          baseline
        )
      );
    },
    [baseline, facetData.setData, priceDropDisplayName]
  );

  const triggerMenuDidExpand = useCallback(() => {
    setDidExpandMenu(true);
  }, []);

  const value = useMemo<FacetDataContextType>(
    () => ({
      baseline,
      didExpandMenu,
      facetData: {
        ...facetData,
        setData: setFacetDataWithMerge
      },
      pagination,
      setFacetData: setFacetDataWithMerge,
      setPagination,
      triggerMenuDidExpand
    }),
    [
      baseline,
      didExpandMenu,
      facetData,
      pagination,
      setFacetDataWithMerge,
      triggerMenuDidExpand
    ]
  );

  return (
    <FacetDataContext.Provider value={value}>
      {props.children}
    </FacetDataContext.Provider>
  );
}

FacetDataContextProvider.displayName = 'FacetDataContextProvider';

export default FacetDataContext;

export const generateFacetDataContext = (
  customState: DeepPartial<FacetDataContextType>
): FacetDataContextType =>
  deepmerge<FacetDataContextType, DeepPartial<FacetDataContextType>>(
    defaultContext,
    customState
  );

export function useFacetData() {
  return useContext(FacetDataContext).facetData.data;
}
